/**
 * Add Kind / Add Domain / Add Relation 공용 모달.
 *
 * 최소 입력(identifier + display name) 만 받는다. 나머지 메타 (color/icon/description 등) 는
 * 추가 후 MetaEditPanel 에서 보강하도록 한다. 심플한 2-step 플로 중 1단계.
 */
import { useEffect, useRef, useState } from "react"

interface AddEntryModalProps {
	readonly type: "kind" | "domain" | "relation"
	readonly onClose: () => void
	readonly onCreate: (input: { readonly name: string; readonly displayName: string }) => void
	readonly existingNames: ReadonlyArray<string>
}

function titleFor(type: AddEntryModalProps["type"]): string {
	if (type === "kind") return "Kind 추가"
	if (type === "domain") return "Domain 추가"
	return "Relation 추가"
}

function hintFor(type: AddEntryModalProps["type"]): string {
	if (type === "kind") return "소문자 + 숫자 + 언더스코어. 예: concept, api_endpoint"
	if (type === "domain") return "소문자 + 숫자 + 하이픈. 예: ops, product-marketing"
	return "UPPER_SNAKE_CASE 권장. 예: RELATES_TO, DEPENDS_ON"
}

export function AddEntryModal(props: AddEntryModalProps) {
	const [name, setName] = useState("")
	const [displayName, setDisplayName] = useState("")
	const [error, setError] = useState<string | undefined>()
	const identifierRef = useRef<HTMLInputElement>(null)

	// 모달이 열리면 identifier 필드로 포커스 이동 (a11y: autoFocus 속성 대신 effect 사용)
	useEffect(() => {
		identifierRef.current?.focus()
	}, [])

	const trimmedName = name.trim()
	const trimmedDisplay = displayName.trim()
	const canSubmit = trimmedName.length > 0 && trimmedDisplay.length > 0

	function onSubmit() {
		if (!canSubmit) return
		if (props.existingNames.includes(trimmedName)) {
			setError(`이미 존재하는 이름입니다: ${trimmedName}`)
			return
		}
		props.onCreate({ name: trimmedName, displayName: trimmedDisplay })
	}

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<form
				className="modal"
				onMouseDown={(e) => e.stopPropagation()}
				onSubmit={(e) => {
					e.preventDefault()
					onSubmit()
				}}
				style={{ maxWidth: 460 }}
			>
				<div className="modal-head">
					<h2>{titleFor(props.type)}</h2>
					<p>{hintFor(props.type)}</p>
				</div>

				<div className="modal-body">
					{error && (
						<div className="alert alert-danger" style={{ marginBottom: 14 }}>
							{error}
						</div>
					)}

					<label className="field">
						<span className="field-label">식별자</span>
						<input
							ref={identifierRef}
							className="input input-mono"
							value={name}
							onChange={(e) => {
								setName(e.target.value)
								setError(undefined)
							}}
							spellCheck={false}
						/>
					</label>

					<label className="field">
						<span className="field-label">표시 이름</span>
						<input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
					</label>
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onClose}>
						취소
					</button>
					<button type="submit" className="btn btn-primary" disabled={!canSubmit}>
						추가
					</button>
				</div>
			</form>
		</dialog>
	)
}
